import React from 'react';
import {
  Typography,
  LinearProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  Stack,
  Avatar,
  useTheme,
} from '@mui/material';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SignalCellularConnectedNoInternet0BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import RouterIcon from '@mui/icons-material/Router';
import SensorsIcon from '@mui/icons-material/Sensors';
import NetworkHealthCard from './NetworkHealthCard';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface MeshStatusProps {
  nodes?: Array<{ id: string; name: string; rssi: number }>;
}

// Master list of nodes that should appear
const DEFAULT_NODE_NAMES = [
  'Gateway1',
  'Node1',
  'Node2',
  'Node3',
  'Node4',
  'Node5',
];

function getSignalStrengthInfo(rssi: number): {
  color: string;
  label: string;
  level: number;
} {
  if (rssi >= -60) return { color: '#4caf50', label: 'Excellent', level: 4 }; // Excellent (Green)
  if (rssi >= -70) return { color: '#8bc34a', label: 'Good', level: 3 }; // Good (Light Green)
  if (rssi >= -80) return { color: '#ffc107', label: 'Fair', level: 2 }; // Fair (Amber)
  if (rssi >= -90) return { color: '#ff9800', label: 'Poor', level: 1 }; // Poor (Orange)
  return { color: '#f44336', label: 'Very Poor', level: 0 }; // Very Poor (Red)
}

function getSignalPercentage(rssi: number): number {
  const min = -180;
  const max = -10;
  const clamped = Math.max(min, Math.min(rssi, max));
  return ((clamped - min) / (max - min)) * 100;
}

const MeshStatus: React.FC<MeshStatusProps> = ({ nodes = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const displayedNodes = DEFAULT_NODE_NAMES.map((name) => {
    const found = nodes.find((n) => n.name === name);
    if (found) {
      return { ...found, offline: false };
    }
    return { id: name, name, rssi: -180, offline: true };
  });

  const onlineNodes = displayedNodes.filter((node) => !node.offline);
  const offlineNodes = displayedNodes.filter((node) => node.offline);

  const finalNodes = [...onlineNodes, ...offlineNodes];

  // Calculate network health statistics
  const totalNodes = finalNodes.length;
  const onlineCount = onlineNodes.length;
  const healthPercentage = Math.round((onlineCount / totalNodes) * 100);

  // Get appropriate color based on health percentage
  const healthColor =
    healthPercentage > 80
      ? '#4caf50'
      : healthPercentage > 60
      ? '#8bc34a'
      : healthPercentage > 40
      ? '#ffc107'
      : healthPercentage > 20
      ? '#ff9800'
      : '#f44336';

  return (
    <NetworkHealthCard>
      {/* Header with improved stats visualization */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t('mesh_status')}
        </Typography>
        <Chip
          icon={<SensorsIcon />}
          label={`${onlineCount}/${totalNodes} Online`}
          sx={{
            backgroundColor: healthColor,
            color: '#fff',
            fontWeight: 'bold',
            '& .MuiChip-icon': { color: '#fff' },
          }}
        />
      </Box>

      {/* Network health indicator */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 0.5,
            color: palette.textPrimary,
          }}
        >
          <Typography variant="body2">
            {t('network_health')}{' '}
            <span style={{ fontWeight: 'bold' }}>{healthPercentage}%</span>
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={healthPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 2,
            backgroundColor: enableDarkTheme
              ? 'rgba(255,255,255,0.1)'
              : 'rgba(0,0,0,0.1)',
            border: `1px solid ${
              enableDarkTheme ? palette.borderGrey : palette.border
            }`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: healthColor,
            },
          }}
        />
      </Box>

      <TableContainer
        sx={{
          maxHeight: '460px',
          borderRadius: 2,
          overflow: 'auto',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.3)'
              : '0 4px 20px rgba(0,0,0,0.1)',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.2)'
                : 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
          },
        }}
      >
        <Table
          sx={{
            minWidth: 300,
            backgroundColor: palette.boxBackground,
            '& .MuiTableCell-root': {
              borderColor: enableDarkTheme
                ? palette.borderGrey
                : palette.border,
            },
          }}
          aria-label={t('mesh_status_table')}
          size="medium"
        >
          <TableHead>
            <TableRow sx={{ color: palette.textPrimary }}>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: palette.textPrimary,
                }}
              >
                {t('asset')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: palette.textPrimary,
                }}
              >
                {t('signal_strength')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: '100px',
                  color: palette.textPrimary,
                }}
              >
                {t('status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {finalNodes.map((node) => {
              if (node.offline) {
                // Node is offline styling
                return (
                  <TableRow
                    key={node.id}
                    hover
                    sx={{
                      opacity: 0.7,
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,76,48,0.05)'
                        : 'rgba(255,76,48,0.05)',
                    }}
                  >
                    <TableCell sx={{ color: palette.textPrimary }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'rgba(255,152,0,0.1)',
                            color: '#ff9800',
                          }}
                        >
                          {node.name === 'Gateway1' ? (
                            <RouterIcon fontSize="small" />
                          ) : (
                            <SensorsIcon fontSize="small" />
                          )}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {node.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: palette.textPrimary }}>
                      <LinearProgress
                        variant="determinate"
                        value={0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: enableDarkTheme
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.1)',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: palette.textPrimary }}>
                      <Chip
                        size="small"
                        label={t('offline')}
                        icon={
                          <SignalCellularConnectedNoInternet0BarIcon fontSize="small" />
                        }
                        sx={{
                          backgroundColor: enableDarkTheme
                            ? 'rgba(255,152,0,0.2)'
                            : 'rgba(255,152,0,0.1)',
                          color: '#ff9800',
                          fontWeight: 'medium',
                          '& .MuiChip-icon': { color: '#ff9800' },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              } else {
                // Node is online styling
                const percentage = getSignalPercentage(node.rssi);
                const { color, label } = getSignalStrengthInfo(node.rssi);

                return (
                  <TableRow
                    key={node.id}
                    hover
                    sx={{
                      backgroundColor: enableDarkTheme
                        ? 'rgba(76,175,80,0.05)'
                        : 'rgba(76,175,80,0.02)',
                    }}
                  >
                    <TableCell sx={{ color: palette.textPrimary }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: enableDarkTheme
                              ? 'rgba(76,175,80,0.15)'
                              : 'rgba(76,175,80,0.1)',
                            color,
                          }}
                        >
                          {node.name === 'Gateway1' ? (
                            <RouterIcon fontSize="small" />
                          ) : (
                            <SensorsIcon fontSize="small" />
                          )}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {node.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: palette.textPrimary }}>
                      <Tooltip
                        title={`${label} signal: ${node.rssi.toFixed(1)} dBm`}
                        arrow
                        placement="top"
                      >
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color }}>
                              {label}
                            </Typography>
                            <Typography variant="caption">
                              {node.rssi.toFixed(1)} dBm
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: enableDarkTheme
                                ? 'rgba(255,255,255,0.1)'
                                : 'rgba(0,0,0,0.1)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: color,
                              },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: palette.textPrimary }}>
                      <Chip
                        size="small"
                        label={t('online')}
                        icon={<SignalCellularAltIcon fontSize="small" />}
                        sx={{
                          backgroundColor: enableDarkTheme
                            ? `${color}20`
                            : `${color}15`,
                          color,
                          fontWeight: 'medium',
                          '& .MuiChip-icon': { color },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              }
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </NetworkHealthCard>
  );
};

export default MeshStatus;
